import React, { useEffect, useMemo, useState } from 'react';
import { W3wIcon } from '@rsos/assets/icons';
import HoveredButton from '@rsos/components/capstone/HoveredButton';
import Loader from '@rsos/components/capstone/Loader';
import Popover from '@rsos/components/capstone/base/Popover/Popover';
import { CAPSTONE, CENTRAL_STATION } from '@rsos/constants/appNames.js';
import { ALERTS, EMERGENCY_CALLS } from '@rsos/constants/incidentTypes';
import useIsBrowserTabActive from '@rsos/hooks/useIsBrowserTabActive';
import { getWhat3Words } from '@rsos/reduxDataStore/src/features/what3words/what3wordsSlice';
import { selectUserPermissionsByApp } from '@rsos/sinatra';
import { useAppDispatch, useAppSelector } from '@rsos/utils/reduxHooks';
import usePortalType from '@rsos/utils/usePortalType';
import { WHAT_3_WORDS } from '../../../constants/permissions';
import { handleCopy } from '../helpers/handleCopy';
import {
  CopyRowBox,
  LeftText,
  LoaderContainer,
  RowBox,
  IconWrapper,
} from '../styles/common.styles';
import { W3WText } from './What3words.styles';
import What3wordsTooltip from './What3wordsTooltip';

const What3words = ({
  location,
  isHoverable,
  irpVersion,
  payloadID,
  requestID,
  isHistoricLocation,
}) => {
  const dispatch = useAppDispatch();

  const { portalType } = usePortalType();

  const isBrowserTabActive = useIsBrowserTabActive();

  const isCentralStation = useAppSelector(
    state => state.sinatra.user.currentRole.application === CENTRAL_STATION,
  );

  const hasWhat3wordsAccess = useAppSelector(state =>
    selectUserPermissionsByApp(state.sinatra.user, CAPSTONE)?.includes(
      WHAT_3_WORDS,
    ),
  );

  const selectedAlertID = useAppSelector(
    state => state.incidents.selectedAlertID,
  );

  const id = location.caller_id || selectedAlertID;

  const latitude = location.latitude || location.geodetic?.latitude;

  const longitude = location.longitude || location.geodetic?.longitude;

  const w3w = useAppSelector(
    state =>
      state.what3words[location.caller_id ? EMERGENCY_CALLS : ALERTS][id]?.[
        `${latitude}_${longitude}`
      ],
  );

  const shouldGetWhat3Words = useMemo(() => {
    return id && latitude && longitude && !w3w?.words;
  }, [id, latitude, longitude, w3w?.words]);

  const [hoveredId, setHoveredId] = useState(null);

  const [isCopied, setIsCopied] = useState();

  const handleHover = w3w => setHoveredId(w3w);

  const handleClick = () => {
    handleCopy({
      value: `https://what3words.com/${w3w?.words}`,
      name: 'what3words',
      irpVersion,
      payloadID,
      requestID,
      portalType,
      onStart: () => {
        setIsCopied(true);
      },
      onEnd: () => {
        setIsCopied(false);
      },
    });
  };

  useEffect(() => {
    if (shouldGetWhat3Words) {
      dispatch(
        getWhat3Words({
          lat: latitude,
          lng: longitude,
          id,
          incidentType: location.caller_id ? EMERGENCY_CALLS : ALERTS,
        }),
      );
    }
  }, [
    dispatch,
    id,
    latitude,
    location.caller_id,
    longitude,
    shouldGetWhat3Words,
  ]);

  // If the current browser tab is not visible, set the hoveredId to null.
  useEffect(() => {
    if (!isBrowserTabActive && hoveredId) handleHover(null);
  }, [isBrowserTabActive, hoveredId]);

  const What3wordsTriggerElement = () => {
    return w3w?.isLoading ? (
      <LoaderContainer>
        <Loader fluid text={''} />
      </LoaderContainer>
    ) : w3w?.words ? (
      <RowBox
        br="5px"
        isHoverable={isHoverable}
        copied={isCopied}
        onMouseEnter={() => handleHover(w3w.words)}
        onMouseLeave={() => handleHover(null)}
      >
        <RowBox>
          <IconWrapper>
            <W3wIcon />
          </IconWrapper>
          <CopyRowBox data-name="what3words">
            <LeftText>what3words:&nbsp;</LeftText>
            <W3WText
              isHistoricLocation={isHistoricLocation}
              data-name="what3words-text"
            >
              {w3w.words}
            </W3WText>
          </CopyRowBox>
        </RowBox>
        {isHoverable && (
          <HoveredButton onClick={handleClick} withIcon isCopied={isCopied} />
        )}
      </RowBox>
    ) : null;
  };

  return !isCentralStation && !hasWhat3wordsAccess ? null : (
    <Popover
      popoverID="what3words-tooltip"
      trigger={<What3wordsTriggerElement />}
      isOpen={hoveredId === w3w?.words}
      cssDisplayProperty="block"
      useCenterXOffset
      useCustomOffset
      yOffset={25}
    >
      <What3wordsTooltip tooltipText={w3w?.words} />
    </Popover>
  );
};

export default What3words;
